class SiteHeader extends HTMLElement {
  constructor() {
    super();

    this.lastScrollYPosition = 0;
    this.scrollThreshold = 50; // Define threshold for scroll direction change

    this.megaMenuListShadows = this.querySelectorAll(
      '.mega-menu__list-wrapper-shadow',
    );
  }

  connectedCallback() {
    window.addEventListener('scroll', this.handleScroll.bind(this));

    // Handle the list shadows
    this.megaMenuListShadows.forEach((shadowEl) =>
      this.handleListShadowsOnScroll(shadowEl),
    );
  }

  /**
   * Handle the scroll event
   */
  handleScroll() {
    const { scrollY } = window;

    // Update last scroll position and handle invisibility based on threshold
    if (scrollY >= this.lastScrollYPosition + this.scrollThreshold) {
      this.toggleHeader(true);
      this.lastScrollYPosition = scrollY;
    } else if (scrollY < this.lastScrollYPosition - this.scrollThreshold) {
      this.toggleHeader(false);
      this.lastScrollYPosition = scrollY;
    }
  }

  /**
   * Toggle the header based on the scroll position
   * @param {boolean} toggleBoolean - The boolean to toggle the header
   */
  toggleHeader(toggleBoolean) {
    const hasOpenMenuItems =
      this.querySelectorAll('menu-item [aria-expanded="true"]').length > 0
        ? true
        : false;

    const hasOpenMenuDrawer = document.querySelector('menu-drawer[aria-hidden="false"]');

    // Do not hide the header if there are open menu items
    if (hasOpenMenuItems || hasOpenMenuDrawer) return;

    document.body.classList.toggle('site-header--hidden', toggleBoolean);
  }

  /**
   * Toggle the menu based on the expanded state
   * @param {object} control - The control element
   * @param {object} target - The target element
   * @param {boolean} expanded - The expanded state of the menu
   */
  toggleItem({ control, target, expanded }) {
    if (!control || !target || expanded === undefined) return;
    control.ariaExpanded = expanded;
    target.ariaHidden = !expanded;
  }

  handleListShadowsOnScroll(shadowEl) {
    const list = shadowEl.parentElement
      .closest('.mega-menu__list-wrapper')
      .querySelector('.mega-menu__list');
    const lastChild = list.querySelector('li:last-child');

    const observer = new IntersectionObserver(
      ([{ isIntersecting }]) => {
        shadowEl.classList.toggle(
          'mega-menu__list-wrapper-shadow--hidden',
          isIntersecting,
        );
      },
      {
        threshold: 1,
      },
    );

    lastChild && observer.observe(lastChild);
  }
}

class MenuItem extends SiteHeader {
  constructor() {
    super();

    this.control = this.querySelector('[aria-controls]');
    this.controlValue = this.control.getAttribute('aria-controls');
    this.target = this.querySelector(`#${this.controlValue}`);
    this.backdrop = this.querySelector('.mega-menu__backdrop');
  }

  connectedCallback() {
    // Close the menu if the user clicks outside of the menu
    document.addEventListener('click', (event) => {
      if (
        this.contains(event.target) ||
        this.control.ariaExpanded === 'false'
      ) {
        return;
      }
      this.toggleItem(false);
    });

    // Toggle the menu if the user clicks the control
    this.control.addEventListener('click', () => {
      this.toggleItem(this.control.ariaExpanded === 'false');
    });

    // Close the menu if the user clicks the backdrop
    this.backdrop.addEventListener('click', () => this.toggleItem(false));
  }

  /**
   * Toggle the menu based on the expanded state
   * @param {boolean} expanded - The expanded state of the menu
   */
  toggleItem(expanded) {
    // Call the parent method to toggle the menu
    super.toggleItem({ control: this.control, target: this.target, expanded });
  }
}

class MegaMenu extends SiteHeader {
  constructor() {
    super();

    this.controls = this.querySelectorAll('[aria-controls]');
    this.backdrop = this.querySelector('.mega-menu__backdrop');
  }

  connectedCallback() {
    // Toggle the menu if the user clicks the control
    this.controls.forEach((control) => {
      if (!control) return;

      const controlValue = control.getAttribute('aria-controls');
      const target = this.querySelector(`#${controlValue}`);

      if (!target) return;

      switch (this.dataset.eventType) {
      case 'hover':
        this.handleHoverEvents({ control, target });
        break;

      case 'click':
        this.handelClickEvents({ control, target });
        break;
      }

      if (this.dataset.eventType === 'hover') {
        control.addEventListener('mouseenter', () => {
          super.toggleItem({ control, target, expanded: true });
        });

        control.addEventListener('mouseleave', () => {
          if (!control.matches(':hover') && !target.matches(':hover')) {
            super.toggleItem({ control, target, expanded: false });
          }
        });

        // Close the menu if the user hovers over the backdrop
        this.backdrop.addEventListener('mouseover', () =>
          super.toggleItem({ control, target, expanded: false }),
        );
      }
    });
  }

  handleHoverEvents({ control, target}) {
    if (this.dataset.eventType === 'hover') {
      control.addEventListener('mouseenter', () => {
        super.toggleItem({ control, target, expanded: true });
      });

      control.addEventListener('mouseleave', () => {
        if (!control.matches(':hover') && !target.matches(':hover')) {
          super.toggleItem({ control, target, expanded: false });
        }
      });

      // Close the menu if the user hovers over the backdrop
      this.backdrop.addEventListener('mouseover', () => {
        super.toggleItem({ control, target, expanded: false });
      });
    }
  }

  handelClickEvents({ control, target }) {
    control.addEventListener('click', () => {
      super.toggleItem({
        control,
        target,
        expanded: control.ariaExpanded === 'false',
      });
    });

    // Close the menu if the user clicks the backdrop
    this.backdrop.addEventListener('click', () =>
      super.toggleItem({ control, target, expanded: false }),
    );
  }
}
class CartIconBubble extends ToggleElementClass {
  constructor() {
    super();
  }

  add() {
    super.add();
    window.tealium.useTealium && tealiumCartDrawerOpen();
  }
}

class ToggleSearch extends ToggleElementClass {
  constructor() {
    super();

    // key up escape
    document.addEventListener('keyup', (event) => {
      if (!document.body.classList.contains('header-search-is-open')) return;

      if(event?.code?.toUpperCase() !== 'ESCAPE') return;

      const { toggleClass } = this.options;
      if (this.target?.classList.contains(toggleClass)) {
        this.remove();
      }
    });
  }

  remove() {
    this.searchToggle = document.querySelector('toggle-search');
    this.searchToggle?.focus();

    super.remove();
  }
}

customElements.define('site-header', SiteHeader);
customElements.define('menu-item', MenuItem);
customElements.define('mega-menu', MegaMenu);
customElements.define('cart-icon-bubble', CartIconBubble);
customElements.define('toggle-search', ToggleSearch);
